import type { Printing } from "@webscan/catalogue.js";
import type { Candidate } from "@webscan/decide.js";
import type { Identification, ScanEvent, ViewEndpoint } from "../protocol/events";
import { sharesArtwork } from "@webscan/decide.js";
import {
  captureKey,
  deleteCapture,
  encodeThumbnail,
  resolveCapture,
  restoreCapture,
  shortlist,
  unresolveCapture,
  updateCaptureEvidence,
  type CaptureIdentity,
  type CaptureVerdict,
  type ResolvedBy,
} from "./captures";
import { log, warn } from "./debug";
import { settleByPrice } from "./price-tie";
import { priceTieCents } from "./settings";

/** A session is durable independently of the capped keepsake-image history. */
export interface SessionFind extends CaptureIdentity {
  id: string;
  at: number;
  /** Stable arrival order for two finds with the same millisecond timestamp. */
  order?: number;
  verdict: CaptureVerdict;
  printing: Printing;
  candidates: Candidate[];
  /** The user's upright image. Never replaced by catalogue artwork. */
  capture: Blob | null;
  /** Small upright JPEG; strings are legacy persisted data URLs, never new object URLs. */
  preview: Blob | string | null;
  captureId: number | null;
  verified: boolean;
  settled: boolean;
  scannedAs: { printing: Printing; verdict: CaptureVerdict } | null;
  /** Who resolved a RESOLVED row: the user, or the price-tie policy at arrival. */
  resolvedBy?: ResolvedBy | null;
}

export interface SessionChange {
  type: "load" | "add" | "update" | "remove";
  id?: string;
  /** Only image/persistence fields changed; identity, valuation and choices did not. */
  mediaOnly?: boolean;
}

const STORE = "finds";
let rows: SessionFind[] = [];
const listeners = new Set<(change: SessionChange) => void>();
const historyListeners = new Set<() => void>();
const touched = new Set<string>();
let database: Promise<IDBDatabase | null> | null = null;
let ready: Promise<void> | null = null;
let writes: Promise<void> = Promise.resolve();
let historyWrites: Promise<void> = Promise.resolve();
let storageIssue: string | null = null;
const mounted = new WeakSet<ViewEndpoint>();
/** A human choice (including undo/discard) ends automatic refinement for that find. */
const reviewed = new Set<string>();
const captureLinks = new Map<string, number>();
type CaptureMedia = Pick<SessionFind, "capture" | "preview" | "captureId">;
// Undo snapshots share media progress without sharing the user's choice. Weak keys
// let a discarded scan's image go once its undo snapshot is no longer retained.
const mediaBySnapshot = new WeakMap<SessionFind, CaptureMedia>();
const pendingMedia = new Map<string, CaptureMedia>();
const mediaJobs = new Set<Promise<void>>();
let lastOrder = 0;

function nextOrder(): number {
  lastOrder = Math.max(Date.now() * 1000, lastOrder + 1);
  return lastOrder;
}

function newestFirst(first: SessionFind, second: SessionFind): number {
  return (
    second.at - first.at ||
    (second.order ?? second.captureId ?? 0) - (first.order ?? first.captureId ?? 0)
  );
}

export function sessionStorageIssue(): string | null {
  return storageIssue;
}

function refused(error: unknown): void {
  if (storageIssue) return;
  const detail = error instanceof Error ? error.message : "browser storage is unavailable";
  storageIssue = `This session is kept in this tab only: ${detail}`;
  warn("boot", storageIssue);
  notify({ type: "update" });
}

function notify(change: SessionChange): void {
  for (const listener of listeners) listener(change);
}

export function subscribeSession(listener: (change: SessionChange) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Keepsake changes are visible only after the current choice/discard reaches history. */
export function subscribeSessionHistory(listener: () => void): () => void {
  historyListeners.add(listener);
  return () => historyListeners.delete(listener);
}

function openDb(): Promise<IDBDatabase | null> {
  database ??= new Promise((resolve) => {
    try {
      // A separate database leaves old keepsake pruning and upgrades unable to remove
      // unresolved cards. Session rows, candidate snapshots and their blobs live here.
      const request = indexedDB.open("webscan2-session", 1);
      request.onupgradeneeded = () => {
        request.result.createObjectStore(STORE, { keyPath: "id" });
      };
      request.onsuccess = () => {
        const db = request.result;
        db.onversionchange = () => db.close();
        resolve(db);
      };
      request.onerror = () => {
        refused(request.error);
        resolve(null);
      };
      request.onblocked = () => {
        refused(new Error("close another scanner tab to enable session storage"));
        resolve(null);
      };
    } catch (error) {
      refused(error);
      resolve(null);
    }
  });
  return database;
}

function readRows(db: IDBDatabase): Promise<SessionFind[]> {
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, "readonly");
      const request = tx.objectStore(STORE).getAll();
      let loaded: SessionFind[] = [];
      request.onsuccess = () => {
        loaded = request.result as SessionFind[];
      };
      tx.oncomplete = () => resolve(loaded);
      tx.onerror = tx.onabort = () => {
        refused(tx.error);
        resolve([]);
      };
    } catch (error) {
      refused(error);
      resolve([]);
    }
  });
}

/** Hydrate once. Finds arriving while IndexedDB opens win over the stored snapshot. */
export function sessionReady(): Promise<void> {
  ready ??= (async () => {
    const db = await openDb();
    const loaded = db ? await readRows(db) : [];
    const liveIds = new Set(rows.map((row) => row.id));
    rows = [...rows, ...loaded.filter((row) => !liveIds.has(row.id) && !touched.has(row.id))];
    rows.sort(newestFirst);
    for (const row of rows) lastOrder = Math.max(lastOrder, row.order ?? 0);
    touched.clear();
    notify({ type: "load" });
  })();
  return ready;
}

/** Newest first; callers must treat both the list and its row snapshots as immutable. */
export function sessionFinds(): readonly SessionFind[] {
  return rows;
}

export function getSessionFind(id: string): SessionFind | undefined {
  return rows.find((row) => row.id === id);
}

export function unresolvedFinds(): SessionFind[] {
  return rows.filter((row) => row.verdict === "AMBIGUOUS");
}

function persist(row: SessionFind | null, id: string): Promise<void> {
  // Serial writes preserve choice → undo ordering even when storage is slow. No scan
  // waits on this chain and refusal always leaves the complete session in memory.
  writes = writes.then(async () => {
    await sessionReady();
    const db = await openDb();
    if (!db) return;
    await new Promise<void>((resolve) => {
      try {
        const tx = db.transaction(STORE, "readwrite");
        if (row) tx.objectStore(STORE).put(row);
        else tx.objectStore(STORE).delete(id);
        tx.oncomplete = () => resolve();
        tx.onerror = tx.onabort = () => {
          refused(tx.error);
          resolve();
        };
      } catch (error) {
        refused(error);
        resolve();
      }
    });
  });
  return writes;
}

/** Useful before a reload/export; scanner arrival never waits for durable storage. */
export async function flushSessionWrites(): Promise<void> {
  await Promise.all([...mediaJobs]);
  await writes;
  await historyWrites;
}

function identify(printing: Printing): CaptureIdentity {
  return {
    key: captureKey(printing),
    name: printing.name,
    setCode: printing.setCode,
    collector: printing.collector,
  };
}

function replace(row: SessionFind, mediaOnly = false): Promise<void> {
  if (row.order === undefined)
    row = { ...row, order: getSessionFind(row.id)?.order ?? nextOrder() };
  touched.add(row.id);
  const previous = getSessionFind(row.id);
  const media = pendingMedia.get(row.id) ?? (previous && mediaBySnapshot.get(previous));
  if (media) mediaBySnapshot.set(row, media);
  const exists = rows.some((item) => item.id === row.id);
  rows = exists ? rows.map((item) => (item.id === row.id ? row : item)) : [row, ...rows];
  rows.sort(newestFirst);
  const saved = persist(row, row.id);
  notify({ type: exists ? "update" : "add", id: row.id, ...(mediaOnly ? { mediaOnly } : {}) });
  return saved;
}

/** Serialize history writes against the current row, so a late choose cannot beat undo. */
function syncHistory(id: string, knownCaptureId: number | null, restore = false): Promise<void> {
  historyWrites = historyWrites.then(async () => {
    const row = getSessionFind(id);
    const captureId = row?.captureId ?? captureLinks.get(id) ?? knownCaptureId;
    if (captureId === null || captureId === undefined) return;
    if (!row) {
      await deleteCapture(captureId);
    } else if (restore && row.capture) {
      const original = row.scannedAs;
      await restoreCapture({
        ...identify(row.printing),
        id: captureId,
        at: row.at,
        verdict: row.verdict,
        illustration: row.printing.illustration,
        candidates: shortlist(row.candidates),
        blob: row.capture,
        scannedAs: original ? { ...identify(original.printing), verdict: original.verdict } : null,
        chosenPrinting: row.verdict === "RESOLVED" ? { ...row.printing } : null,
        resolvedBy: row.verdict === "RESOLVED" ? (row.resolvedBy ?? "user") : null,
      });
    } else if (row.verdict === "RESOLVED") {
      await resolveCapture(captureId, row.printing, row.printing, row.resolvedBy ?? "user");
    } else {
      await unresolveCapture(captureId);
      if (row.verdict === "ACCEPTED" || row.verdict === "AMBIGUOUS")
        await updateCaptureEvidence(captureId, row.printing, row.verdict, row.candidates);
    }
    for (const listener of historyListeners) listener();
  });
  return historyWrites;
}

/**
 * Add once per found event. Resolution never mutates the engine's identification.
 *
 * One resolution happens here rather than in a review: an ambiguous read whose tied top
 * printings are worth about the same (`settleByPrice`, threshold from Settings) arrives
 * already RESOLVED to the more valuable one, `resolvedBy: "price"`, with the engine's
 * reading kept as `scannedAs` exactly as a human choice keeps it. It never enters the
 * review queue; undo puts it back there.
 */
export function addSessionFind(
  event: ScanEvent,
  preview?: SessionFind["preview"],
): SessionFind | null {
  const { identification } = event;
  const { printing, verdict } = identification;
  if (!printing || (verdict !== "ACCEPTED" && verdict !== "AMBIGUOUS")) return null;
  const scanned: SessionFind = {
    ...identify(printing),
    id: event.readId ?? crypto.randomUUID(),
    at: Date.now(),
    order: nextOrder(),
    verdict,
    printing: { ...printing },
    candidates: identification.candidates.map((candidate) => ({
      ...candidate,
      printing: { ...candidate.printing },
    })),
    capture: null,
    preview: preview ?? null,
    captureId: null,
    verified: identification.verified,
    settled: identification.settled,
    scannedAs: null,
  };
  const tie = settleByPrice(identification, priceTieCents());
  const row: SessionFind = tie
    ? {
        ...scanned,
        ...identify(tie.printing),
        printing: { ...tie.printing },
        verdict: "RESOLVED",
        resolvedBy: "price",
        scannedAs: { printing: { ...printing }, verdict },
      }
    : scanned;
  if (tie) {
    log(
      "verdict",
      `settled by price: ${tie.group.length} printings of ${printing.name} tie at ` +
        `${tie.percent}% and lie within ${(tie.spreadCents / 100).toFixed(2)} ${tie.currency} ` +
        `of each other (threshold ${(priceTieCents() / 100).toFixed(2)}) — taking ` +
        `${tie.printing.setCode} ${tie.printing.collector}, the more valuable one`,
    );
  }
  const media: CaptureMedia = { capture: null, preview: row.preview, captureId: null };
  pendingMedia.set(row.id, media);
  void replace(row);
  const applyMedia = async (patch: Partial<CaptureMedia>): Promise<void> => {
    Object.assign(media, patch);
    const current = getSessionFind(row.id);
    // Image work may finish after discard. Only attach it to a row still in the
    // session; the undo snapshot retains media without resurrecting the scan.
    if (current) await replace({ ...current, ...patch }, true);
  };
  const previewJob =
    preview === undefined
      ? new Promise<void>((resolve) => setTimeout(resolve, 0)).then(async () =>
          applyMedia({ preview: await encodeThumbnail(event.card, event.identification.flipped) }),
        )
      : Promise.resolve();
  const captureJob = event.capture.then((capture) => applyMedia({ capture }));
  const linkJob = event.captureId.then(async (captureId) => {
    if (captureId === null) return;
    captureLinks.set(row.id, captureId);
    await applyMedia({ captureId });
    // Always synchronize the latest choice, including discard/undo while encoding.
    await syncHistory(row.id, captureId);
  });
  const job = Promise.allSettled([previewJob, captureJob, linkJob])
    .then((results) => {
      for (const result of results) {
        if (result.status === "rejected")
          warn("capture", "could not attach scan image", result.reason);
      }
    })
    .finally(() => {
      pendingMedia.delete(row.id);
      mediaJobs.delete(job);
    });
  mediaJobs.add(job);
  return row;
}

export function mountScanSession(endpoint: ViewEndpoint): void {
  if (mounted.has(endpoint)) return;
  mounted.add(endpoint);
  endpoint.on("found", (event) => {
    addSessionFind(event);
  });
  endpoint.on("found:updated", ({ readId, identification }) => {
    void refineSessionFind(readId, identification);
  });
  void sessionReady();
}

/** Apply OCR to the same row, preserving its position, image and all human decisions. */
export async function refineSessionFind(id: string, result: Identification): Promise<boolean> {
  const row = getSessionFind(id);
  if (
    !row ||
    reviewed.has(id) ||
    row.verdict !== "AMBIGUOUS" ||
    result.verdict !== "ACCEPTED" ||
    !result.printing ||
    !row.candidates[0] ||
    !result.candidates[0] ||
    !sharesArtwork(row.candidates[0], result.candidates[0])
  )
    return false;
  const next = {
    ...row,
    ...identify(result.printing),
    printing: { ...result.printing },
    verdict: result.verdict,
    candidates: result.candidates.map((c) => ({ ...c, printing: { ...c.printing } })),
    verified: result.verified,
    settled: result.settled,
  };
  await replace(next);
  await syncHistory(id, next.captureId);
  return true;
}

/** Returns the previous snapshot for the review toast's undo. */
export async function resolveSessionFind(
  id: string,
  printing: Printing,
): Promise<SessionFind | null> {
  const previous = getSessionFind(id);
  if (!previous) return null;
  reviewed.add(id);
  const next: SessionFind = {
    ...previous,
    ...identify(printing),
    printing: { ...printing },
    verdict: "RESOLVED",
    resolvedBy: "user",
    scannedAs: previous.scannedAs ?? {
      printing: { ...previous.printing },
      verdict: previous.verdict,
    },
  };
  await replace(next);
  await syncHistory(id, next.captureId);
  return previous;
}

/** Restore a choice snapshot with any image that finished since the snapshot was taken. */
export async function restoreSessionFind(snapshot: SessionFind): Promise<void> {
  reviewed.add(snapshot.id);
  const current = getSessionFind(snapshot.id);
  const row = {
    ...snapshot,
    ...(mediaBySnapshot.get(snapshot) ?? {}),
    ...(current ? { capture: current.capture, preview: current.preview } : {}),
    captureId: current?.captureId ?? captureLinks.get(snapshot.id) ?? snapshot.captureId,
  };
  const media = mediaBySnapshot.get(snapshot);
  if (media) mediaBySnapshot.set(row, media);
  await replace(row);
  await syncHistory(row.id, row.captureId, !current);
}

export async function unresolveSessionFind(id: string): Promise<void> {
  const row = getSessionFind(id);
  if (!row?.scannedAs) return;
  const { printing, verdict } = row.scannedAs;
  await restoreSessionFind({
    ...row,
    ...identify(printing),
    printing,
    verdict,
    scannedAs: null,
    resolvedBy: null,
  });
}

/** Discard removes the session row and its linked history image; no hidden queue cap. */
export async function discardSessionFind(id: string): Promise<SessionFind | null> {
  const row = getSessionFind(id);
  if (!row) return null;
  reviewed.add(id);
  touched.add(id);
  rows = rows.filter((item) => item.id !== id);
  const saved = persist(null, id);
  notify({ type: "remove", id });
  await saved;
  await syncHistory(id, row.captureId);
  return row;
}
